/**
 * Import required libraries
 */
const { produce } = require("immer");
const { createTheme } = require("@material-ui/core/styles");

// merge color objects { ligth, main, dark }
const colorMerge = (src = {}, ref = {}, form = {}, def = {}, keys = ["light", "main", "dark"]) => {
  keys.forEach(k => src[k] = form[k] || ref[k] || def[k] || src[k] || null)
  return src
}

// extract color from string (ex: linear-gradient(to right, #444444, #000))
const extractColor = str => {
  const regex = /(#([0-9a-f]{3}){1,2}|(rgba|hsla)\(\d{1,3}%?(,\s?\d{1,3}%?){2},\s?(1|0|0?\.\d+)\)|(rgb|hsl)\(\d{1,3}%?(,\s?\d{1,3}%?){2}\))/i
  let res = str.match(regex)
  return res[0]
}

// get color value from color object depending on type and contrast
function augmented(theme, dark, color, contrast = false) {
  if (color.split("gradient").length > 1) {
    color = extractColor(color)
  }
  let augm = theme.palette.augmentColor({ main: color });
  if (dark) {
    if (contrast) return augm.light;
    else return augm.dark;
  } else return augm.main;
}

function findThemePalette(key) {
  if (typeof key !== "string") return null
  if (key in this.palette) return this.palette[key]
  return key.split(".").reduce((val, key) => {
    if (val instanceof Object && key in val) return val[key]
    return null
  }, this.palette)
}

function getColor(c, def) {
  if (!c) return def ? this.getColor(def) : "inherit"
  if (c[0] === "#" || c.includes("rgb")) return c
  let color = this.findThemePalette(c)
  if (!color) return def ? this.getColor(def) : "inherit"
  if (color instanceof Object) {
    const dark = this.palette.type === "dark"
    return dark ? color.light : color.main
  }
  return this.getColor(color, def)
}

module.exports = function (theme, data = {}, form = {}) {

  if (!("palette" in data)) data.palette = {}
  if (!("palette" in form)) form.palette = {}
  let type = form.type || data.type || theme.palette.type
  const dark = type === "dark"
  const inner = createTheme({
    palette: produce(theme.palette, draft => {

      // palette functions customized

      draft.getContrastText = color => {
        if (color.split("gradient").length > 1) {
          color = augmented(theme, true, extractColor(color))
        }
        return theme.palette.getContrastText(color)
      }
      draft.augmentColor = obj => {
        if (obj.main.split("gradient").length > 1) {
          return {
            light: obj.main,
            main: obj.main,
            dark: obj.main
          }
        } else return theme.palette.augmentColor(obj)
      }
      const augmentColor = c => c ? draft.augmentColor({ main: c }) : undefined

      // palette builder

      draft.type = dark ? "dark" : "light";

      // primary color
      draft.primary = colorMerge(draft.primary, augmentColor(data.palette.primary), augmentColor(form.palette.primary), {
        light: "#4688CC",
        main: "#3C72AA",
        dark: "#2d5883",
        contrastText: theme.palette.getContrastText(dark ? "#2d5883" : "#4688CC")
      })

      // secondary color
      draft.secondary = colorMerge(draft.secondary, augmentColor(data.palette.secondary), augmentColor(form.palette.secondary), {
        light: "#D1D1D1",
        main: "#918e8e",
        dark: "#545454",
        contrastText: theme.palette.getContrastText(dark ? "#545454" : "#D1D1D1")
      })

      // appbar color
      draft.appbar = colorMerge(draft.appbar, augmentColor(data.palette.appbar), augmentColor(form.palette.appbar), {
        light: "#D1D1D1",
        main: "#F1F1F1",
        dark: "#545454"
      })

      // link color
      draft.link = colorMerge(draft.link, augmentColor(data.palette.link), augmentColor(form.palette.link), {
        light: "#5232ff",
        main: "#8A74FF",
        dark: "#a08efe"
      })

      // background color
      draft.background = /*colorMerge(draft.background, data.palette.background, form.palette.background, */{
        default: dark ? "#303030" : "#f6f6f6",
        paper: dark ? "#424242" : "#fff",
        body: dark ? "#505050" : "#d5d3d3"
      }/*, ["default", "paper", "body"])*/

      // success color
      draft.success = colorMerge(draft.success, augmentColor(data.palette.success), augmentColor(form.palette.success))

      // selected color
      draft.selected = colorMerge(draft.selected, augmentColor(data.palette.selected), augmentColor(form.palette.success), draft.success)

      draft.toolbar = draft.toolbar || data.palette.toolbar || form.palette.toolbar

      // text color
      draft.text.hint = dark ? "rgba(255, 255, 255, 0.80)" : "rgba(0, 0, 0, 0.7)";
      draft.text.primary = dark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)"
      draft.text.secondary = dark ? "rgba(255, 255, 255, 0.54)" : "rgba(0, 0, 0, 0.54)"
      draft.text.disabled = dark ? "rgba(255, 255, 255, 0.38)" : "rgba(0, 0, 0, 0.38)"

      // info color
      draft.info = colorMerge(draft.info, augmentColor(data.palette.info), augmentColor(form.palette.info))
      // warning color
      draft.warning = colorMerge(draft.warning, augmentColor(data.palette.warning), augmentColor(form.palette.warning))
      // error color
      draft.error = colorMerge(draft.error, augmentColor(data.palette.error), augmentColor(form.palette.error))

      // buttons color
      draft.buttons = Object.assign({}, draft.buttons, data.palette.buttons, form.palette.buttons)

      // select color
      draft.select = Object.assign({}, draft.select, data.palette.select, form.palette.select)

      // textField color
      draft.textField = Object.assign({}, draft.textField, data.palette.textField, form.palette.textField)

      // switch color
      draft.switch = Object.assign({}, draft.switch, data.palette.switch, form.palette.switch)

      // checkbox color
      draft.checkbox = Object.assign({}, draft.checkbox, data.palette.checkbox, form.palette.checkbox)

      // slider color
      draft.slider = Object.assign({}, draft.slider, data.palette.slider, form.palette.slider)

      // logo background
      draft.logoBackground = form.palette.logoBaground || data.palette.logoBaground || draft.logoBackground || null
    }),
    typography: {
      fontFamily:
        (data.selectedTypos) ?
          (data.selectedTypos === "Bahnschrift Condensed") ?
            ["Bahnschrift"] :
            [data.selectedTypos].concat(["Bahnschrift"])
          : ['Bahnschrift'],
      button: {
        textTransform: "none"
      }
    }
  });
  inner.findThemePalette = findThemePalette.bind(inner)
  inner.getColor = getColor.bind(inner)
  return inner
}
